import { useState, useEffect, useMemo } from "react";
import { Field, Toggle } from "./components.js";
import { getTaxFields, TAX_SYSTEMS, CANADA_MODES, fmtCurrency } from "./utils.js";
import {
  createProduct,
  getAllProducts,
  updateProduct,
  permanentlyDeleteProduct,
} from "./db/index.js";


// ══════════════════════════════════════════════════════════════════════════════
// SESSION STORAGE OPERATIONS (FOR FREE TIER)
// ══════════════════════════════════════════════════════════════════════════════

const SESSION_STORAGE_KEY = "invoice_gen_products_session";

/**
 * Get products from sessionStorage (FREE tier only)
 * @returns {Array} Array of products
 */
const getProductsFromSession = () => {
  try {
    const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error("Failed to load products from sessionStorage:", error);
    return [];
  }
};

/**
 * Save products to sessionStorage (FREE tier only)
 * @param {Array} products - Array of products to save
 */
const saveProductsToSession = (products) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(products));
  } catch (error) {
    console.error("Failed to save products to sessionStorage:", error);
    throw error;
  }
};

/**
 * Add new product to sessionStorage (FREE tier only)
 * @param {object} productData - Product data to add
 * @returns {object} Created product with generated ID
 */
const addProductToSession = (productData) => {
  const products = getProductsFromSession();
  const newProduct = {
    ...productData,
    id: `prod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    archived: false
  };
  products.push(newProduct);
  saveProductsToSession(products);
  return newProduct;
};

/**
 * Update product in sessionStorage (FREE tier only)
 * @param {string} productId - Product ID to update
 * @param {object} updates - Fields to update
 * @returns {object|null} Updated product or null if not found
 */
const updateProductInSession = (productId, updates) => {
  const products = getProductsFromSession();
  const index = products.findIndex(p => p.id === productId);
  if (index !== -1) {
    products[index] = {
      ...products[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    saveProductsToSession(products);
    return products[index];
  }
  return null;
};

/**
 * Delete product from sessionStorage (FREE tier only)
 * @param {string} productId - Product ID to delete
 */
const deleteProductFromSession = (productId) => {
  const products = getProductsFromSession();
  const filtered = products.filter(p => p.id !== productId);
  saveProductsToSession(filtered);
};


// ══════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════


const StyledInput = ({ value, onChange, placeholder, onClear, hasValue, G, maxLength, isPrice }) => (
  <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="text"
        inputMode={isPrice ? "decimal" : "text"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        style={{
  flex: 1, padding: "8px 10px", border: `1.5px solid ${G.border}`, borderRadius: 6,
  fontSize: 13, fontFamily: "'Sora', sans-serif", outline: "none", color: G.ink,
  background: G.surface, transition: "border-color 0.15s"
}}
        onFocus={e => e.target.style.borderColor = G.accent}
        onBlur={e => e.target.style.borderColor = G.border}
      />
      <button
        disabled={!hasValue}
        onClick={onClear}
        style={{
          width: 24, height: 24,
          background: hasValue ? G.dangerBg : G.surface2,
          color: hasValue ? G.danger : G.muted,
          border: `1.5px solid ${hasValue ? G.danger : G.border}40`,
          borderRadius: "50%", fontSize: 14, cursor: hasValue ? "pointer" : "not-allowed",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "sans-serif", flexShrink: 0, transition: "all 0.15s"
        }}
        title="Clear"
      >
        ✕
      </button>
    </div>
  </div>
);


// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════


const CATEGORIES = ["Product", "Service", "Subscription"];


export default function ProductCatalogue({ G, products, setProducts, brand, settings, user }) {
  const taxSystem = brand.taxSystem || "GST_INDIA";
  const canadaMode = brand.canadaMode || "GST_PST";
  const taxFields = getTaxFields(taxSystem, canadaMode);
  const darkMode = G.bg === "#1C1C1E";


  const fmtPlain = (n) => fmtCurrency(n, "", "no-symbol", "", "international", "comma-dot", true);


  const empty = {
    name: "",
    price: "",
    unit: "",
    tax: "",
    tax2: "",
    barcode: "",
    productId: "",
    hsn: "",
    customField1Value: "",
    customField2Value: "",
    category: "Product" // Default category
  };
 
  // Modal & Form State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [modalForm, setModalForm] = useState(empty);
  const [modalErrors, setModalErrors] = useState({});
  const [editingProduct, setEditingProduct] = useState(null); // Store full product object
  const [clearNext, setClearNext] = useState(false);
  const [btnPulse, setBtnPulse] = useState(false);
  const [saving, setSaving] = useState(false);


  // Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [filterCategory, setFilterCategory] = useState(""); // Category filter
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");


  // ══════════════════════════════════════════════════════════════════════════════
  // LOAD PRODUCTS ON MOUNT (TIER-BASED)
  // ══════════════════════════════════════════════════════════════════════════════


  useEffect(() => {
    const loadProducts = async () => {
      try {
        if (user.tierLevel === 0) {
          // ═══════════════════════════════════════════════════════════════════
          // FREE TIER - Load from sessionStorage
          // ═══════════════════════════════════════════════════════════════════
          const sessionProducts = getProductsFromSession();
          setProducts(sessionProducts);
          
        } else {
          // ═══════════════════════════════════════════════════════════════════
          // PAID TIER - Load from database
          // ═══════════════════════════════════════════════════════════════════
          const allProducts = await getAllProducts();
          setProducts(allProducts);
        }
      } catch (error) {
        console.error("Failed to load products:", error);
        alert("Failed to load products. Please refresh the page.");
      }
    };
   
    loadProducts();
  }, [setProducts, user.tierLevel]);


  // ══════════════════════════════════════════════════════════════════════════════
  // VALIDATION
  // ══════════════════════════════════════════════════════════════════════════════


  const validateTax = (v) => {
    if (v === "" || v === undefined) return "";
    const n = parseFloat(v);
    if (isNaN(n)) return "Must be a number";
    if (n < 0) return "Cannot be negative";
    if (n > 100) return "Cannot exceed 100";
    return "";
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // MODAL HANDLERS
  // ══════════════════════════════════════════════════════════════════════════════


  const openModal = (mode, product = null) => {
    setModalMode(mode);
    setClearNext(false);
   
    if (mode === "edit" && product) {
      setEditingProduct(product);
      setModalForm({
        name: product.name || "",
        price: String(product.price || ""),
        unit: product.unit || "",
        tax: String(product.tax || ""),
        tax2: String(product.tax2 || ""),
        barcode: product.barcode || "",
        productId: product.productId || "",
        hsn: product.hsn || "",
        customField1Value: product.customField1Value || "",
        customField2Value: product.customField2Value || "",
        category: product.category || "Product"
      });
    } else {
      setEditingProduct(null);
      setModalForm(empty);
    }
   
    setModalErrors({});
    setShowModal(true);
  };


  const closeModal = () => {
    setShowModal(false);
    setClearNext(false);
    setSaving(false);
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // SAVE HANDLER (CREATE OR UPDATE) - TIER-BASED WITH LIMIT CHECK
  // ══════════════════════════════════════════════════════════════════════════════


  const handleSaveModal = async () => {
    // ────────────────────────────────────────────────────────────────────────
    // STEP 1: VALIDATE FORM DATA
    // ────────────────────────────────────────────────────────────────────────
    
    const newErrors = {};
    taxFields.forEach(f => {
      const e = validateTax(modalForm[f.key]);
      if (e) newErrors[f.key] = e;
    });
   
    if (!modalForm.name || !modalForm.price) {
      setModalErrors(newErrors);
      return;
    }
   
    if (Object.keys(newErrors).length) {
      setModalErrors(newErrors);
      return;
    }

    // ────────────────────────────────────────────────────────────────────────
    // STEP 2: CHECK PRODUCT LIMIT (Only for new products)
    // ────────────────────────────────────────────────────────────────────────
    
    if (modalMode === "add") {
  const currentCount = products.length;
  const limit = user.usage?.productLimit || (user.tierLevel === 0 ? 5 : null);
  
  // Only check limit if it's defined (null = unlimited)
  if (limit !== null && currentCount >= limit) {
        alert(
          `Product Limit Reached!\n\n` +
          `Your ${user.tier} tier allows ${limit} product${limit !== 1 ? 's' : ''}.\n\n` +
          `Upgrade to add more products.`
        );
        return;
      }
    }

    setSaving(true);
    setBtnPulse(true);

    try {
      const productData = {
        name: modalForm.name,
        price: parseFloat(modalForm.price),
        unit: modalForm.unit,
        tax: modalForm.tax === "" ? 0 : parseFloat(modalForm.tax),
        tax2: modalForm.tax2 === "" ? 0 : parseFloat(modalForm.tax2 || 0),
        barcode: modalForm.barcode,
        productId: modalForm.productId,
        hsn: modalForm.hsn,
        customField1Value: modalForm.customField1Value,
        customField2Value: modalForm.customField2Value,
        category: modalForm.category || "Product"
      };

      if (user.tierLevel === 0) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - SessionStorage Path (No Database)
        // ═══════════════════════════════════════════════════════════════════
        
        if (modalMode === "edit" && editingProduct) {
          // Update existing product in sessionStorage
          const updated = updateProductInSession(editingProduct.id, productData);
          if (!updated) {
            throw new Error("Failed to update product in session storage");
          }
        } else {
          // Create new product in sessionStorage
          addProductToSession(productData);
        }

        // Reload from sessionStorage
        const freshProducts = getProductsFromSession();
        setProducts(freshProducts);

      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Database Path
        // ═══════════════════════════════════════════════════════════════════
        
        if (modalMode === "edit" && editingProduct) {
          // UPDATE existing product in database
          await updateProduct(editingProduct.id, productData);
        } else {
          // CREATE new product in database
          await createProduct(productData);
        }

        // Reload products from DB
        const freshProducts = await getAllProducts();
        setProducts(freshProducts);
      }

      // Keep modal open until animation finishes
      setTimeout(() => {
        setBtnPulse(false);
        setSaving(false);
       
        if (clearNext && modalMode === "add") {
          // Clear form for next entry
          setModalForm(empty);
          setModalErrors({});
        } else {
          // Close modal
          closeModal();
        }
      }, 450);


    } catch (error) {
      console.error("Failed to save product:", error);
      
      // ✅ IMPROVED: Better error messaging with details
      let errorMessage = "Failed to save product. Please try again.";
      
      if (error.name === 'ValidationError') {
        errorMessage = `Validation Error: ${error.message}`;
      } else if (error.name === 'PermissionDeniedError') {
        errorMessage = "Permission denied. Upgrade required for this feature.";
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
      setBtnPulse(false);
      setSaving(false);
    }
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // DELETE HANDLER (PERMANENT - NO RECOVERY) - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════


  const handleDelete = async (product) => {
    // ✅ CHANGED: Updated confirmation for permanent deletion
    if (!window.confirm(
      `⚠️ Permanently Delete "${product.name}"?\n\n` +
      `This will permanently remove this product from your catalogue.\n\n` +
      `⚠️ This action CANNOT be undone.\n\n` +
      `Note: To delete all products with 30-day recovery, use "Delete Product Data" in Settings.\n\n` +
      `Continue with permanent deletion?`
    )) {
      return;
    }


    try {
      if (user.tierLevel === 0) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - Delete from sessionStorage
        // ═══════════════════════════════════════════════════════════════════
        
        deleteProductFromSession(product.id);
        
        // Reload from sessionStorage
        const freshProducts = getProductsFromSession();
        setProducts(freshProducts);
        
      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Permanent delete from database
        // ═══════════════════════════════════════════════════════════════════
        
        await permanentlyDeleteProduct(product.id);
        
        // Reload products from DB
        const freshProducts = await getAllProducts();
        setProducts(freshProducts);
      }
      
    } catch (error) {
      console.error("Failed to delete product:", error);
      
      // ✅ IMPROVED: Better error messaging
      let errorMessage = "Failed to delete product. Please try again.";
      
      if (error.name === 'PermissionDeniedError') {
        errorMessage = "Permission denied. You don't have access to delete products.";
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // SORTING
  // ══════════════════════════════════════════════════════════════════════════════


  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };


  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span style={{ color: "#FFFFFF", fontSize: "14px", marginLeft: "4px", opacity: 0.5, fontWeight: 600 }}>⇅</span>;
    }
    return sortDirection === "asc"
      ? <span style={{ color: "#FFFFFF", fontSize: "14px", marginLeft: "4px", fontWeight: 700 }}>▲</span>
      : <span style={{ color: "#FFFFFF", fontSize: "14px", marginLeft: "4px", fontWeight: 700 }}>▼</span>;
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // FILTERING & SORTING
  // ══════════════════════════════════════════════════════════════════════════════


  const processedProducts = useMemo(() => {
    let filtered = products.filter(p => {
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesText =
          (p.name || "").toLowerCase().includes(q) ||
          (p.productId || "").toLowerCase().includes(q) ||
          (p.hsn || "").toLowerCase().includes(q) ||
          (p.barcode || "").toLowerCase().includes(q) ||
          (p.price || "").toString().includes(q);
        if (!matchesText) return false;
      }
     
      // Price filter
      const price = parseFloat(p.price) || 0;
      if (minPrice !== "" && !isNaN(minPrice) && price < parseFloat(minPrice)) return false;
      if (maxPrice !== "" && !isNaN(maxPrice) && price > parseFloat(maxPrice)) return false;
     
      // Category filter
      if (filterCategory && p.category !== filterCategory) return false;
     
      return true;
    });


    // Sort
    return filtered.sort((a, b) => {
      let valA, valB;
     
      switch (sortColumn) {
        case "name":
          valA = (a.name || "").toLowerCase();
          valB = (b.name || "").toLowerCase();
          break;
        case "productId":
          valA = (a.productId || "").toLowerCase();
          valB = (b.productId || "").toLowerCase();
          break;
        case "hsn":
          valA = (a.hsn || "").toLowerCase();
          valB = (b.hsn || "").toLowerCase();
          break;
        case "barcode":
          valA = (a.barcode || "").toLowerCase();
          valB = (b.barcode || "").toLowerCase();
          break;
        case "price":
          valA = parseFloat(a.price) || 0;
          valB = parseFloat(b.price) || 0;
          break;
        case "tax":
          valA = a.tax || 0;
          valB = b.tax || 0;
          break;
        case "tax2":
          valA = a.tax2 || 0;
          valB = b.tax2 || 0;
          break;
        case "category":
          valA = (a.category || "").toLowerCase();
          valB = (b.category || "").toLowerCase();
          break;
        case "customField1":
          valA = (a.customField1Value || "").toLowerCase();
          valB = (b.customField1Value || "").toLowerCase();
          break;
        case "customField2":
          valA = (a.customField2Value || "").toLowerCase();
          valB = (b.customField2Value || "").toLowerCase();
          break;
        default:
          return 0;
      }


      if (typeof valA === "number" && typeof valB === "number") {
        return sortDirection === "asc" ? valA - valB : valB - valA;
      }
     
      valA = String(valA);
      valB = String(valB);
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [products, searchQuery, minPrice, maxPrice, filterCategory, sortColumn, sortDirection]);


  // ══════════════════════════════════════════════════════════════════════════════
  // TABLE COLUMNS
  // ══════════════════════════════════════════════════════════════════════════════

const tableColumns = [
  { key: "productId", label: "Product ID / Code", sortable: true, width: "10%" },
  { key: "name", label: "Name", sortable: true, width: "15%" },
  { key: "category", label: "Category", sortable: true, width: "8%" },
  { key: "unit", label: "Unit", sortable: false, width: "6%" },
  { key: "hsn", label: "HSN/Commodity", sortable: true, width: "8%" },
  { key: "barcode", label: "Barcode", sortable: true, width: "8%" },
  { key: "price", label: "Price", sortable: true, width: "8%" },
  ...(settings.customField1?.enabled ? [{ key: "customField1", label: settings.customField1.name || "CF1", sortable: true, width: "7%" }] : []),
  ...(settings.customField2?.enabled ? [{ key: "customField2", label: settings.customField2.name || "CF2", sortable: true, width: "7%" }] : []),
  ...taxFields.map(f => ({ key: f.key, label: f.label, sortable: true, width: "7%" })),
  { key: "actions", label: "Actions", sortable: false, width: "10%" }
];
  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════════


  return (
    <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
      {/* Header Row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, color: G.ink, marginBottom: 4 }}>
            📦 Products & Services
          </h2>
<div style={{ color: G.muted, fontSize: 13 }}>
  {products.length} product{products.length !== 1 ? "s" : ""} total
  {user.tierLevel === 0 ? (
    <span style={{ color: G.warning, fontWeight: 500 }}>
      {" "}• {products.length}/5 used
    </span>
  ) : user.usage?.productLimit ? (
    <span style={{ color: G.accent, fontWeight: 500 }}>
      {" "}• Limit: {user.usage.productLimit}
    </span>
  ) : (
    <span style={{ color: G.success, fontWeight: 500 }}>
      {" "}• Unlimited
    </span>
  )}
</div>
        </div>
 <button
  onClick={() => openModal("add")}
  style={{
    background: darkMode ? G.surface2 : G.ink,
    color: darkMode ? G.ink : G.surface,
    border: darkMode ? `2px solid ${G.border}` : "none",
    borderRadius: 7,
    padding: "7px 15px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Sora', sans-serif",
    display: "flex",
    alignItems: "center",
    gap: 6,
    boxShadow: G.shadow
  }}
>
  ➕ Add Product / Service
</button>
      </div>


      {/* Tax Context Banner */}
      <div style={{
  background: G.accentBg,
  border: `1px solid ${G.accent}88`,
  borderRadius: 8,
  padding: "8px 14px",
  marginBottom: 16,
  fontSize: 12,
  color: G.ink,
  fontWeight: 600,
  letterSpacing: "0.3px"
}}>
        Active Tax System: {TAX_SYSTEMS.find(t => t.value === taxSystem)?.label || "Not set"}
        {taxSystem === "CANADA" && ` — ${CANADA_MODES.find(m => m.value === canadaMode)?.label}`}
        {taxSystem === "NONE" && " — no tax fields shown"}
      </div>

      {/* Filter Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr 1fr 1fr", gap: 12, marginBottom: 16, alignItems: "flex-start" }}>
        {/* Search Card */}
        <div style={{
          background: G.surface,
          border: `1.5px solid ${G.border}`,
          borderRadius: 9,
          padding: "10px 14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
        }}>
          <label style={{
            fontSize: 10, fontWeight: 600, color: G.muted, letterSpacing: .5, textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 4, marginBottom: 6
          }}>
            <span style={{ fontSize: 12 }}>🔍</span> Search
          </label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Product Name / ID / Code / Price"
              maxLength={100}
              style={{
  flex: 1, padding: "8px 10px", border: `1.5px solid ${G.border}`, borderRadius: 6,
  fontSize: 13, fontFamily: "'Sora', sans-serif", outline: "none", color: G.ink,
  background: G.surface, transition: "border-color 0.15s"
}}
              onFocus={e => e.target.style.borderColor = G.accent}
              onBlur={e => e.target.style.borderColor = G.border}
            />
            <button
              disabled={!searchQuery}
              onClick={() => setSearchQuery("")}
              style={{
                background: searchQuery ? G.accentBg : G.surface2,
                color: searchQuery ? G.accent : G.muted,
                border: `1.5px solid ${searchQuery ? G.accent : G.border}40`,
                borderRadius: 6, padding: "5px 12px", fontSize: 12,
                cursor: searchQuery ? "pointer" : "not-allowed",
                fontFamily: "'Sora', sans-serif", fontWeight: 500, whiteSpace: "nowrap",
                transition: "all 0.15s", flexShrink: 0
              }}
              onMouseEnter={e => {
                if (searchQuery) {
                  e.target.style.background = G.accent;
                  e.target.style.color = G.accentFg;
                  e.target.style.border = "1.5px solid transparent";
                }
              }}
              onMouseLeave={e => {
                e.target.style.background = searchQuery ? G.accentBg : G.surface2;
                e.target.style.color = searchQuery ? G.accent : G.muted;
                e.target.style.border = `1.5px solid ${searchQuery ? G.accent : G.border}40`;
              }}
            >
              Clear
            </button>
          </div>
          <div style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>{searchQuery.length}/100 characters</div>
        </div>
{/* Category Filter */}
<div style={{ background: G.surface, border: `1.5px solid ${G.border}`, borderRadius: 9, padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
  <label style={{ fontSize: 10, fontWeight: 600, color: G.muted, letterSpacing: .5, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
    🏷️ Category
  </label>
  <select
    value={filterCategory}
    onChange={(e) => setFilterCategory(e.target.value)}
    style={{
  width: "100%",
  padding: "8px 10px",
  border: `1.5px solid ${G.border}`,
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "'Sora', sans-serif",
  outline: "none",
  color: G.ink,
  background: G.surface,
  cursor: "pointer"
}}
  >
    <option value="">All Categories</option>
    {CATEGORIES.map(cat => (
      <option key={cat} value={cat}>{cat}</option>
    ))}
  </select>
  <div style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>
    {filterCategory ? `Filtered: ${filterCategory}` : "‎ "}
  </div>
</div>
        {/* Min Price Card */}
        <div style={{ background: G.surface, border: `1.5px solid ${G.border}`, borderRadius: 9, padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: G.muted, letterSpacing: .5, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
            💰 Min Price
          </label>
          <StyledInput
            value={minPrice}
            onChange={(e) => {
              let val = e.target.value.replace(/[^\d.]/g, '');
              if (val === '.') val = '0.';
              const parts = val.split('.');
              if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
              if (parts[1] && parts[1].length > 2) val = parts[0] + '.' + parts[1].slice(0, 2);
              if (val.length > 12) val = val.slice(0, 12);
              setMinPrice(val);
            }}
            placeholder=""
            onClear={() => setMinPrice("")}
            hasValue={!!minPrice}
            G={G}
            maxLength={12}
            isPrice={true}
          />
          <div style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>{minPrice.length}/12 characters</div>
        </div>


        {/* Max Price Card */}
        <div style={{ background: G.surface, border: `1.5px solid ${G.border}`, borderRadius: 9, padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: G.muted, letterSpacing: .5, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
            💰 Max Price
          </label>
          <StyledInput
            value={maxPrice}
            onChange={(e) => {
              let val = e.target.value.replace(/[^\d.]/g, '');
              if (val === '.') val = '0.';
              const parts = val.split('.');
              if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
              if (parts[1] && parts[1].length > 2) val = parts[0] + '.' + parts[1].slice(0, 2);
              if (val.length > 12) val = val.slice(0, 12);
              setMaxPrice(val);
            }}
            placeholder=""
            onClear={() => setMaxPrice("")}
            hasValue={!!maxPrice}
            G={G}
            maxLength={12}
            isPrice={true}
          />
          <div style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>{maxPrice.length}/12 characters</div>
        </div>
      </div>


      {/* Products Table */}
      <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, overflow: "auto", marginBottom: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr style={{ background: G.surface2 }}>
              {tableColumns.map(col => (
                <th
  key={col.key}
  onClick={() => col.sortable && handleSort(col.key)}
  onMouseEnter={col.sortable ? e => e.target.style.background = "rgba(0,0,0,0.08)" : undefined}
onMouseLeave={col.sortable ? e => e.target.style.background = "transparent" : undefined}
  style={{
    color: G.ink,
    padding: "10px 14px",
    textAlign: col.key === "actions" ? "center" : "left",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: .6,
    textTransform: "uppercase",
    cursor: col.sortable ? "pointer" : "default",
    userSelect: col.sortable ? "none" : "auto",
    transition: "background 0.15s",
    whiteSpace: "nowrap",
    width: col.width  // ✅ ADDED
  }}
>
                  {col.label} {col.sortable && <SortIcon column={col.key} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedProducts.length === 0 ? (
              <tr>
                <td colSpan={tableColumns.length} style={{ padding: "40px 20px", textAlign: "center", color: G.muted, fontSize: 13 }}>
                  No products match your filters
                </td>
              </tr>
            ) : (
              processedProducts.map((p, i) => (
                <tr
                  key={p.id}
                  style={{
                    borderTop: `1px solid ${G.border}`,
                    background: i % 2 === 0 ? G.surface : G.surface2,
                    transition: "background .1s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = G.accentBg}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? G.surface : G.surface2}
                >
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, fontFamily: "monospace", width: "10%" }}>
  {p.productId || "—"}
</td>
<td style={{ padding: "12px 14px", fontWeight: 500, width: "15%" }}>{p.name}</td>
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, width: "8%" }}>{p.category || "—"}</td>
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, width: "6%" }}>{p.unit || "—"}</td>
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, fontFamily: "monospace", width: "8%" }}>
  {p.hsn || "—"}
</td>
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, fontFamily: "monospace", width: "8%" }}>
  {p.barcode || "—"}
</td>
<td style={{ padding: "12px 14px", fontWeight: 500, width: "8%" }}>{fmtPlain(p.price)}</td>

{settings.customField1?.enabled && (
  <td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, width: "7%" }}>
    {p.customField1Value || "—"}
  </td>
)}

{settings.customField2?.enabled && (
  <td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, width: "7%" }}>
    {p.customField2Value || "—"}
  </td>
)}

{taxFields.map(f => (
  <td key={f.key} style={{ padding: "12px 14px", width: "7%" }}>
    {p[f.key] || 0}%
  </td>
))}

<td style={{ padding: "12px 14px", width: "10%" }}>
                    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                      <button
                        onClick={() => openModal("edit", p)}
                        style={{
                          background: G.accentBg,
                          color: G.accent,
                          border: `1.5px solid ${G.accent}40`,
                          borderRadius: 6,
                          padding: "5px 12px",
                          fontSize: 12,
                          cursor: "pointer",
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          transition: "all 0.15s",
                          minWidth: 48,
                          textAlign: "center"
                        }}
                        onMouseEnter={e => {
                          e.target.style.background = G.accent;
                          e.target.style.color = G.accentFg;
                          e.target.style.border = "1.5px solid transparent";
                        }}
                        onMouseLeave={e => {
                          e.target.style.background = G.accentBg;
                          e.target.style.color = G.accent;
                          e.target.style.border = `1.5px solid ${G.accent}40`;
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(p)}
                        style={{
                          background: G.dangerBg,
                          color: G.danger,
                          border: `1.5px solid ${G.danger}40`,
                          borderRadius: 6,
                          padding: "5px 12px",
                          fontSize: 12,
                          cursor: "pointer",
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          transition: "all 0.15s",
                          minWidth: 48,
                          textAlign: "center"
                        }}
                        onMouseEnter={e => {
                          e.target.style.background = G.danger;
                          e.target.style.color = "#FFFFFF";
                          e.target.style.border = "1.5px solid transparent";
                        }}
                        onMouseLeave={e => {
                          e.target.style.background = G.dangerBg;
                          e.target.style.color = G.danger;
                          e.target.style.border = `1.5px solid ${G.danger}40`;
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>


      {/* Add / Edit Modal */}
      {showModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999, padding: "20px"
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "28px",
            maxWidth: 960, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            maxHeight: "90vh", overflowY: "auto", border: `1px solid ${G.border}`
          }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16, color: G.ink }}>
              {modalMode === "edit" ? "✏️ Edit Product" : "➕ Add Product / Service"}
            </div>


            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12, alignItems: "start" }}>
              <Field label="Product / Service Name*">
                <input
                  value={modalForm.name}
                  onChange={e => setModalForm(p => ({ ...p, name: e.target.value }))}
                  placeholder=""
                  maxLength={100}
                />
                <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                  {modalForm.name.length}/100 characters
                </div>
              </Field>


              <Field label="Price*">
                <input
                  type="text"
                  inputMode="decimal"
                  value={modalForm.price}
                  onChange={e => {
                    let val = e.target.value.replace(/[^\d.]/g, '');
                    if (val === '.') val = '0.';
                    const parts = val.split('.');
                    if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
                    if (parts[1] && parts[1].length > 2) val = parts[0] + '.' + parts[1].slice(0, 2);
                    if (val.length > 12) val = val.slice(0, 12);
                    setModalForm(p => ({ ...p, price: val }));
                  }}
                  placeholder="0.00"
                />
                <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                  {(modalForm.price || "").length}/12 characters
                </div>
              </Field>


              <Field label="Category">
                <select
                  value={modalForm.category}
                  onChange={e => setModalForm(p => ({ ...p, category: e.target.value }))}
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    border: `1.5px solid ${G.border}`,
                    borderRadius: 7,
                    fontSize: 14,
                    fontFamily: "'Sora', sans-serif",
                    outline: "none",
                    background: G.surface,
                    color: G.ink,
                    cursor: "pointer"
                  }}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </Field>


              <Field label="Unit">
                <input
                  value={modalForm.unit || ""}
                  onChange={e => setModalForm(p => ({ ...p, unit: e.target.value }))}
                  placeholder=""
                  maxLength={15}
                />
                <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                  {(modalForm.unit || "").length}/15 characters
                </div>
              </Field>


              <Field label="Barcode">
                <input
                  value={modalForm.barcode}
                  onChange={e => setModalForm(p => ({ ...p, barcode: e.target.value }))}
                  placeholder=""
                  maxLength={20}
                />
                <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                  {(modalForm.barcode || "").length}/20 characters
                </div>
              </Field>


              <Field label="SKU, Product ID / Code">
                <input
                  value={modalForm.productId}
                  onChange={e => setModalForm(p => ({ ...p, productId: e.target.value }))}
                  placeholder=""
                  maxLength={40}
                />
                <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                  {(modalForm.productId || "").length}/40 characters
                </div>
              </Field>


              <Field label="Commodity / CN / HSN Code">
                <input
                  value={modalForm.hsn}
                  onChange={e => setModalForm(p => ({ ...p, hsn: e.target.value }))}
                  placeholder=""
                  maxLength={15}
                />
                <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                  {(modalForm.hsn || "").length}/15 characters
                </div>
              </Field>
            </div>


            {/* Tax Fields */}
            {taxFields.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${taxFields.length}, 1fr)`, gap: 16, marginBottom: 16 }}>
                {taxFields.map(f => (
                  <Field key={f.key} label={f.label} error={modalErrors[f.key]}>
                    <input
                      type="number"
                      value={modalForm[f.key]}
                      onChange={e => {
                        setModalForm(p => ({ ...p, [f.key]: e.target.value }));
                        setModalErrors(p => ({ ...p, [f.key]: validateTax(e.target.value) }));
                      }}
                      placeholder="%"
                      maxLength={6}
                      style={{ borderColor: modalErrors[f.key] ? "#C53030" : undefined }}
                    />
                  </Field>
                ))}
              </div>
            )}


            {/* Custom Fields */}
            {(settings.customField1?.enabled || settings.customField2?.enabled) && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                {settings.customField1?.enabled && (
                  <Field label={`${settings.customField1.name || 'Custom Field 1'} (optional)`}>
                    <input
                      value={modalForm.customField1Value || ""}
                      onChange={e => setModalForm(p => ({ ...p, customField1Value: e.target.value }))}
                      placeholder=""
                      maxLength={30}
                    />
                    <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                      {(modalForm.customField1Value || "").length}/30 characters
                    </div>
                  </Field>
                )}
                {settings.customField2?.enabled && (
                  <Field label={`${settings.customField2.name || 'Custom Field 2'} (optional)`}>
                    <input
                      value={modalForm.customField2Value || ""}
                      onChange={e => setModalForm(p => ({ ...p, customField2Value: e.target.value }))}
                      placeholder=""
                      maxLength={30}
                    />
                    <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                      {(modalForm.customField2Value || "").length}/30 characters
                    </div>
                  </Field>
                )}
              </div>
            )}


            {/* Modal Actions */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 20,
              marginTop: 24,
              paddingTop: 16,
              borderTop: `1px solid ${G.border}`
            }}>
              {modalMode === "add" && (
                <div style={{ marginInlineEnd: "auto" }}>
                  <Toggle G={G} label="Keep Form Open" value={clearNext} onChange={setClearNext} />
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={closeModal}
                  disabled={saving}
                  style={{
                    padding: "8px 16px",
                    border: `1px solid ${G.border}`,
                    borderRadius: 7,
                    background: G.surface2,
                    color: G.ink,
                    cursor: saving ? "not-allowed" : "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    opacity: saving ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveModal}
                  disabled={saving}
                  className={btnPulse ? "btn-pulse" : ""}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: 7,
                    background: saving ? G.muted : G.accent,
                    color: G.accentFg,
                    cursor: saving ? "not-allowed" : "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  {saving ? "Saving..." : (modalMode === "edit" ? "Update Product" : "Save Product")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
